import { useEffect, useRef } from 'react'
import Image from 'next/image'
import { trackHeroVideoAutoplayStarted } from '../events'

const POSTER_IMG_URL =
  'https://static.masterclass.com/pen5690/hp_inno_poster.png'

const AV1_360P = 'https://static.masterclass.com/pen5690/opt/hero-360.av1.webm'
const H264_360P = 'https://static.masterclass.com/pen5690/opt/hero-360.h264.mp4'
const VIDEO_SRC = 'https://static.masterclass.com/pen5690/hpinno_hero.mp4'

const MAX_VIDEO_LOAD_TIMEOUT = 2000

// Utility to observe LCP with error tolerance
const observeLCP = (callback: () => void): (() => void) => {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return () => {}
  }

  try {
    const observer = new PerformanceObserver((list) => {
      if (list.getEntries().length > 0) callback()
    })
    observer.observe({ type: 'largest-contentful-paint', buffered: true })
    return () => observer.disconnect()
  } catch (e) {
    return () => {}
  }
}

export const HeroVideo = ({
  className,
  prefersReducedMotion,
}: {
  className: string
  prefersReducedMotion: boolean
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const posterRef = useRef<HTMLImageElement>(null)
  const hasTrackedAutoplay = useRef(false)

  const opacityTransition = 'Dm2akz'
  const hideClass = 'M8f4yx'

  useEffect(() => {
    if (prefersReducedMotion) return () => {}

    const v = videoRef.current
    let videoLoaded = false

    const canplay = () => {
      videoRef.current
        ?.play()
        .then(() => {
          // Only hide poster and show video if play succeeds
          posterRef.current?.classList.add(hideClass)
          videoRef.current?.classList.remove(hideClass)

          // Track only once
          if (!hasTrackedAutoplay.current) {
            hasTrackedAutoplay.current = true
            trackHeroVideoAutoplayStarted()
          }
        })
        .catch(() => {
          reportError('[5690] Hero video autoplay failed')
        })
    }

    const loadVideo = () => {
      if (!videoLoaded && v) {
        videoLoaded = true
        invokeWhenIdle(() => v.load())
      }
    }

    v?.addEventListener('canplay', canplay)

    // LCP detection with timeout fallback
    const maxTimeout = setTimeout(loadVideo, MAX_VIDEO_LOAD_TIMEOUT)
    const cleanupLCP = observeLCP(() => {
      clearTimeout(maxTimeout)
      loadVideo()
    })

    return () => {
      v?.removeEventListener('canplay', canplay)
      clearTimeout(maxTimeout)
      cleanupLCP()
    }
  }, [prefersReducedMotion])

  return (
    <>
      <style>
        {`
          .${opacityTransition} { transition: opacity 0.3s ease; }
          .${hideClass} { opacity: 0; }
        `}
      </style>
      <Image
        ref={posterRef}
        className={`${className} ${opacityTransition}`}
        src={POSTER_IMG_URL}
        alt=''
        width={1920}
        height={1080}
        sizes='100vw'
        priority
        loading='eager'
        fetchPriority='high'
        decoding='sync'
      />
      <video
        ref={videoRef}
        className={`${className} ${opacityTransition} ${hideClass}`}
        preload='none'
        playsInline
        muted
        loop
      >
        <source
          media='(max-width: 600px)'
          src={AV1_360P}
          type='video/webm; codecs=av01'
        />
        <source
          media='(max-width: 600px)'
          src={H264_360P}
          type='video/mp4; codecs=avc1'
        />
        {/* full size for large screens, plus fallback for unsupported browsers */}
        <source src={VIDEO_SRC} type='video/mp4' />
      </video>
    </>
  )
}

/**
 * Safari does not support `requestIdleCallback`
 */
const invokeWhenIdle = (cb: () => void): (() => void) => {
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    const handle = window.requestIdleCallback(cb)
    return () => window.cancelIdleCallback(handle)
  } else {
    const handle = setTimeout(cb, 20)
    return () => clearTimeout(handle)
  }
}
